import type { PageType } from 'types';
import type { ProductsHightlightModule } from 'types/capi/modules';
import type { Affiliate } from 'types/capi/shared';

import AffiliatePageLink from '~/components/AffiliatePageLink';
import DiscountCode from '~/components/DiscountCode';
import Image from '~/components/Image';
import ProductCard from '~/components/ProductCard';

type Props = {
  module: ProductsHightlightModule;
  moduleIndex?: number;
};

type ProductsHighlightProps = Props & { pageType: PageType };

export default function ProductsHighlight({
  module,
  moduleIndex,
  pageType
}: ProductsHighlightProps) {
  return (
    <div className="relative">
      <div>
        <div className="min-w-full">
          <BackgroundImage module={module} />
          <Content
            module={module}
            moduleIndex={moduleIndex}
            pageType={pageType}
          />
        </div>
      </div>
    </div>
  );
}

function Content({ module, moduleIndex, pageType }: ProductsHighlightProps) {
  const isLargerProductSet = module.items.length > 2;

  const containerClassName = isLargerProductSet
    ? 'relative flex w-full flex-col md:max-w-desktop mx-auto'
    : 'relative flex w-full flex-col md:flex-row md:gap-10 justify-between md:max-w-desktop mx-auto';

  return (
    <div className="flex items-end px-4 py-10 pb-14 md:items-center md:pt-20 xl:px-0">
      <div className={containerClassName}>
        <Description module={module} alwaysHideCta={isLargerProductSet} />
        <Products
          module={module}
          moduleIndex={moduleIndex}
          pageType={pageType}
        />
        <Cta
          module={module}
          className={`flex ${isLargerProductSet ? 'self-center' : 'md:hidden'}`}
        />
      </div>
    </div>
  );
}

function Products({
  module,
  moduleIndex = 0,
  pageType
}: ProductsHighlightProps) {
  return (
    <div className="custom-scrollbar flex gap-2 overflow-x-scroll pt-6 sm:gap-6 md:mt-0">
      {module.items.map((product, productIndex) => (
        <ProductCard.External
          allowNotifications
          cardSize="small"
          className="min-w-[168px] max-w-full text-white md:max-w-[264px]"
          key={`product-${product.id}-${productIndex}`}
          padImage
          pagePosition={`Sale Products Highlight-${moduleIndex}`}
          position={productIndex + 1}
          product={product}
          pageType={pageType}
        />
      ))}
    </div>
  );
}

function BackgroundImage({ module }: Props) {
  return (
    <>
      <Image
        alt="home-page-usps-image"
        src={module.background_image.url}
        className="absolute block size-full object-cover"
        loading="eager"
        responsiveOptions={{ xs: { height: 672, retainRatio: true } }}
      />
    </>
  );
}

function Description({
  alwaysHideCta,
  module
}: Props & { alwaysHideCta: boolean }) {
  return (
    <div className="flex flex-col gap-2 md:min-w-[50%] md:max-w-[450px] md:pt-8">
      <h2 className="relative text-[32px] leading-10 text-white md:text-[40px]">
        {module.header}
      </h2>
      <h3 className="relative mt-2 text-base text-white md:text-lg">
        {module.description}
      </h3>
      <Cta
        module={module}
        className={`hidden ${alwaysHideCta ? '' : 'md:mt-10 md:flex'}`}
      />
    </div>
  );
}

function Cta({ className, module }: Props & { className: string }) {
  if (!module.cta) return null;

  return (
    <div className={`flex size-full flex-col gap-4 md:max-w-96 ${className}`}>
      <DiscountCode {...module.discount_code} />
      <AffiliatePageLink
        variant="forest"
        className="flex h-12 items-center justify-center gap-2"
        affiliate={module.cta.affiliate}
        urlTemplates={module.cta}
      >
        {module.cta.affiliate && (
          <AffiliateLogo affiliate={module.cta.affiliate} />
        )}
        {module.cta.text}
      </AffiliatePageLink>
    </div>
  );
}

function AffiliateLogo({ affiliate }: { affiliate: Affiliate }) {
  return (
    <Image
      src={affiliate.logo}
      className="w-7 rounded bg-white"
      alt={affiliate.name}
      loading="lazy"
      height={150}
      width={150}
      responsiveOptions={{ sm: { height: 150, pad: true, width: 150 } }}
      pad
    />
  );
}
